import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import {
  EnvelopeSimpleIcon,
  PencilSimpleIcon,
  PlusIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { api } from "@/convex/_generated/api";
import { useLocalization } from "@/context/LocalizationContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const CATEGORY_OPTIONS = [
  "Entertainment",
  "Productivity",
  "Utilities",
  "Shopping",
  "Health",
  "Education",
  "Finance",
  "Other",
];

const CURRENCY_OPTIONS = ["usd", "eur", "gbp"];

const EMPTY_FORM = {
  name: "",
  amount: "",
  currency: "usd",
  billingDate: "",
  frequency: "monthly",
  category: "Other",
};

const translateCategory = (category, t) => {
  const normalizedCategory = category || "Other";
  const key = `dashboard.categories.${normalizedCategory}`;
  const translated = t(key);
  return translated === key ? normalizedCategory : translated;
};

const translateSource = (subscription, t) => {
  if (subscription.sourceType === "email_import") {
    return subscription.sourceProvider === "google"
      ? t("dashboard.source.googleImport")
      : t("dashboard.source.microsoftImport");
  }

  return t("dashboard.source.manual");
};

const SpendBreakdown = ({ totals, accent = false }) => {
  const { formatCurrency, getCurrencyLabel } = useLocalization();

  if (!totals.length) {
    return (
      <p
        className={`font-outfit text-4xl tracking-tight ${accent ? "text-[#002FA7] dark:text-blue-400" : "text-zinc-900 dark:text-zinc-100"}`}
      >
        0
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {totals.map((total) => (
        <div key={total.currency}>
          <p
            className={`font-outfit tracking-tight ${totals.length === 1 ? "text-4xl" : "text-2xl"} ${accent ? "text-[#002FA7] dark:text-blue-400" : "text-zinc-900 dark:text-zinc-100"}`}
          >
            {formatCurrency(total.amount, total.currency)}
          </p>
          {totals.length > 1 && (
            <p className="mt-1 text-xs font-medium uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">
              {getCurrencyLabel(total.currency)}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

const SubscriptionForm = ({
  formData,
  setFormData,
  onSubmit,
  submitLabel,
  testPrefix,
}) => {
  const { t, getCurrencyLabel } = useLocalization();

  return (
    <form onSubmit={onSubmit} className="space-y-4 mt-4">
      <div>
        <label className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 mb-2 block">
          {t("dashboard.form.serviceName")}
        </label>
        <input
          data-testid={`${testPrefix}-name-input`}
          type="text"
          required
          className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-4 py-2 focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] focus:outline-none"
          value={formData.name}
          onChange={(event) =>
            setFormData({ ...formData, name: event.target.value })
          }
        />
      </div>
      <div>
        <label className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 mb-2 block">
          {t("dashboard.form.amount")}
        </label>
        <input
          data-testid={`${testPrefix}-amount-input`}
          type="number"
          step="0.01"
          required
          className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-4 py-2 focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] focus:outline-none"
          value={formData.amount}
          onChange={(event) =>
            setFormData({ ...formData, amount: event.target.value })
          }
        />
      </div>
      <div>
        <label className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 mb-2 block">
          {t("dashboard.form.currency")}
        </label>
        <Select
          value={formData.currency}
          onValueChange={(value) =>
            setFormData({ ...formData, currency: value })
          }
        >
          <SelectTrigger
            data-testid={`${testPrefix}-currency-select`}
            className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none">
            {CURRENCY_OPTIONS.map((currency) => (
              <SelectItem key={currency} value={currency}>
                {`${currency.toUpperCase()} - ${getCurrencyLabel(currency)}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 mb-2 block">
          {t("dashboard.form.billingDate")}
        </label>
        <input
          data-testid={`${testPrefix}-billing-date-input`}
          type="number"
          min="1"
          max="31"
          required
          className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none px-4 py-2 focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] focus:outline-none"
          value={formData.billingDate}
          onChange={(event) =>
            setFormData({ ...formData, billingDate: event.target.value })
          }
        />
      </div>
      <div>
        <label className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 mb-2 block">
          {t("dashboard.form.frequency")}
        </label>
        <Select
          value={formData.frequency}
          onValueChange={(value) =>
            setFormData({ ...formData, frequency: value })
          }
        >
          <SelectTrigger
            data-testid={`${testPrefix}-frequency-select`}
            className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none">
            <SelectItem value="monthly">
              {t("dashboard.frequency.monthly")}
            </SelectItem>
            <SelectItem value="annual">
              {t("dashboard.frequency.annual")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 mb-2 block">
          {t("dashboard.form.category")}
        </label>
        <Select
          value={formData.category}
          onValueChange={(value) =>
            setFormData({ ...formData, category: value })
          }
        >
          <SelectTrigger
            data-testid={`${testPrefix}-category-select`}
            className="w-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none">
            {CATEGORY_OPTIONS.map((category) => (
              <SelectItem key={category} value={category}>
                {translateCategory(category, t)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <button
        data-testid={`${testPrefix}-submit-button`}
        type="submit"
        className="w-full bg-[#002FA7] text-white rounded-none px-6 py-2.5 hover:bg-[#001B61] transition-colors font-medium"
      >
        {submitLabel}
      </button>
    </form>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const subscriptions = useQuery(api.subscriptions.listSubscriptions);
  const analytics = useQuery(api.subscriptions.getAnalytics);
  const createSubscription = useMutation(api.subscriptions.createSubscription);
  const updateSubscription = useMutation(api.subscriptions.updateSubscription);
  const deleteSubscription = useMutation(api.subscriptions.deleteSubscription);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterFrequency, setFilterFrequency] = useState("all");
  const [formData, setFormData] = useState(EMPTY_FORM);

  const filteredSubscriptions = (subscriptions || []).filter((subscription) => {
    const matchesSearch = `${subscription.name} ${subscription.category || ""}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesFrequency =
      filterFrequency === "all" || subscription.frequency === filterFrequency;
    return matchesSearch && matchesFrequency;
  });

  const resetForm = () => setFormData(EMPTY_FORM);

  const handleAddSubscription = async (event) => {
    event.preventDefault();
    try {
      await createSubscription({
        name: formData.name,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        billingDate: parseInt(formData.billingDate, 10),
        frequency: formData.frequency,
        category: formData.category || undefined,
      });
      toast.success(t("dashboard.list.addSuccess"));
      resetForm();
      setIsAddDialogOpen(false);
    } catch (error) {
      toast.error(error.message || t("dashboard.list.addFailed"));
    }
  };

  const handleEditSubscription = async (event) => {
    event.preventDefault();
    try {
      await updateSubscription({
        subscriptionId: editingSubscription.id,
        name: formData.name,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        billingDate: parseInt(formData.billingDate, 10),
        frequency: formData.frequency,
        category: formData.category || undefined,
      });
      toast.success(t("dashboard.list.updateSuccess"));
      setEditingSubscription(null);
      setIsEditDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.message || t("dashboard.list.updateFailed"));
    }
  };

  const handleDeleteSubscription = async (subscriptionId) => {
    if (!window.confirm(t("dashboard.list.deleteConfirm"))) {
      return;
    }

    try {
      await deleteSubscription({ subscriptionId });
      toast.success(t("dashboard.list.deleteSuccess"));
    } catch (error) {
      toast.error(error.message || t("dashboard.list.deleteFailed"));
    }
  };

  const openEditDialog = (subscription) => {
    setEditingSubscription(subscription);
    setFormData({
      name: subscription.name,
      amount: subscription.amount.toString(),
      currency: subscription.currency || "usd",
      billingDate: subscription.billingDate.toString(),
      frequency: subscription.frequency,
      category: subscription.category || "Other",
    });
    setIsEditDialogOpen(true);
  };

  if (subscriptions === undefined || analytics === undefined) {
    return (
      <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-6 text-zinc-500 dark:text-zinc-400">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <p className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
          {t("dashboard.overviewEyebrow")}
        </p>
        <div>
          <h1 className="font-outfit text-3xl sm:text-4xl tracking-tight text-zinc-900 dark:text-zinc-100">
            {t("dashboard.title")}
          </h1>
          <p className="mt-2 font-dmsans text-sm sm:text-base text-zinc-600 dark:text-zinc-300 leading-relaxed">
            {t("dashboard.description")}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-5">
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-2">
            {t("dashboard.stats.totalSubscriptions")}
          </p>
          <p className="font-outfit text-4xl tracking-tight text-zinc-900 dark:text-zinc-100">
            {analytics.totalSubscriptions}
          </p>
        </div>
        <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-5">
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-2">
            {t("dashboard.stats.monthlyCost")}
          </p>
          <SpendBreakdown totals={analytics.monthlyTotals} accent />
        </div>
        <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-5">
          <p className="uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-2">
            {t("dashboard.stats.annualCost")}
          </p>
          <SpendBreakdown totals={analytics.annualTotals} />
        </div>
      </div>

      <section className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-5 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="font-outfit text-2xl tracking-tight text-zinc-900 dark:text-zinc-100">
              {t("dashboard.list.title")}
            </h2>
            <p className="font-dmsans text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              {t("dashboard.list.description")}
            </p>
          </div>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="add-subscription-button"
                className="flex items-center justify-center gap-2 bg-[#002FA7] text-white rounded-none px-4 py-2 hover:bg-[#001B61] transition-colors font-medium text-sm"
              >
                <PlusIcon size={18} weight="bold" />
                {t("dashboard.list.addButton")}
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none">
              <DialogHeader>
                <DialogTitle className="font-outfit text-2xl tracking-tight text-zinc-900 dark:text-zinc-100">
                  {t("dashboard.list.addTitle")}
                </DialogTitle>
              </DialogHeader>
              <SubscriptionForm
                formData={formData}
                setFormData={setFormData}
                onSubmit={handleAddSubscription}
                submitLabel={t("dashboard.form.addSubmit")}
                testPrefix="add"
              />
            </DialogContent>
          </Dialog>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="search-subscriptions"
            type="text"
            placeholder={t("dashboard.list.searchPlaceholder")}
            className="flex-1 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-none px-4 py-2 focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] focus:outline-none text-sm text-zinc-900 dark:text-zinc-100"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
          <Select value={filterFrequency} onValueChange={setFilterFrequency}>
            <SelectTrigger
              data-testid="filter-frequency"
              className="w-full sm:w-48 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-none text-sm"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none">
              <SelectItem value="all">
                {t("dashboard.list.allFrequencies")}
              </SelectItem>
              <SelectItem value="monthly">
                {t("dashboard.frequency.monthly")}
              </SelectItem>
              <SelectItem value="annual">
                {t("dashboard.frequency.annual")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {filteredSubscriptions.length === 0 ? (
          <div className="border border-dashed border-zinc-300 dark:border-zinc-600 p-10 text-center">
            <p className="font-dmsans text-zinc-500 dark:text-zinc-400">
              {subscriptions.length === 0
                ? t("dashboard.list.noSubscriptions")
                : t("dashboard.list.noMatches")}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-zinc-200 dark:border-zinc-700">
                <tr>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                    {t("dashboard.list.table.service")}
                  </th>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                    {t("dashboard.list.table.amount")}
                  </th>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                    {t("dashboard.list.table.billing")}
                  </th>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                    {t("dashboard.list.table.frequency")}
                  </th>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                    {t("dashboard.list.table.source")}
                  </th>
                  <th className="text-left px-4 py-3 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                    {t("dashboard.list.table.category")}
                  </th>
                  <th className="text-right px-4 py-3 uppercase text-xs tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                    {t("dashboard.list.table.actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.map((subscription) => (
                  <tr
                    key={subscription.id}
                    data-testid={`subscription-row-${subscription.id}`}
                    className="border-b border-zinc-200 dark:border-zinc-700 last:border-b-0"
                  >
                    <td className="px-4 py-4 font-medium text-zinc-900 dark:text-zinc-100">
                      {subscription.name}
                    </td>
                    <td className="px-4 py-4 text-zinc-900 dark:text-zinc-100">
                      {formatCurrency(
                        subscription.amount,
                        subscription.currency,
                      )}
                    </td>
                    <td className="px-4 py-4 text-zinc-900 dark:text-zinc-100">
                      {t("dashboard.list.billingDay", {
                        billingDate: subscription.billingDate,
                      })}
                    </td>
                    <td className="px-4 py-4 text-zinc-900 dark:text-zinc-100">
                      {t(`dashboard.frequency.${subscription.frequency}`)}
                    </td>
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center border border-zinc-200 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-700 px-2 py-1 text-xs font-medium text-zinc-700 dark:text-zinc-200">
                        {translateSource(subscription, t)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-zinc-900 dark:text-zinc-100">
                      {translateCategory(subscription.category, t)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          data-testid={`edit-subscription-${subscription.id}`}
                          onClick={() => openEditDialog(subscription)}
                          className="p-2 text-zinc-600 dark:text-zinc-400 hover:text-[#002FA7] hover:bg-zinc-100 dark:hover:bg-zinc-700 transition-colors"
                        >
                          <PencilSimpleIcon size={18} weight="regular" />
                        </button>
                        <button
                          data-testid={`delete-subscription-${subscription.id}`}
                          onClick={() =>
                            handleDeleteSubscription(subscription.id)
                          }
                          className="p-2 text-zinc-600 dark:text-zinc-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        >
                          <TrashIcon size={18} weight="regular" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="font-dmsans font-medium text-zinc-900 dark:text-zinc-100">
            {t("dashboard.emailImportCard.title")}
          </p>
          <p className="font-dmsans text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            {t("dashboard.emailImportCard.description")}
          </p>
        </div>
        <button
          onClick={() => navigate("/settings/imports")}
          className="flex items-center justify-center gap-2 bg-white dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-none px-4 py-2 border border-zinc-200 dark:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-600 transition-colors font-medium text-sm"
        >
          <EnvelopeSimpleIcon size={16} weight="regular" />
          {t("dashboard.emailImportCard.cta")}
        </button>
      </section>

      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-none">
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl tracking-tight text-zinc-900 dark:text-zinc-100">
              {t("dashboard.list.editTitle")}
            </DialogTitle>
          </DialogHeader>
          <SubscriptionForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleEditSubscription}
            submitLabel={t("dashboard.form.updateSubmit")}
            testPrefix="edit"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
